import {
  WSButton,
  WSCard,
  WSDivider,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSPillProps,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { IWorkItem } from "@wingspanhq/payments/dist/interfaces";
import { IWorkDefinitionResponse } from "@wingspanhq/payments/dist/interfaces/api/workDefinition";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useMemo } from "react";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { AcceptedValuesResources } from "../FormPartialAttributes/helpers";
import { Item } from "./Item";
import { useModalAddItem } from "./ModalAddItem";

type Props = {
  shimmer?: boolean;
  noEdit?: boolean;
  payerEdit?: boolean;

  workLogId: string;
  payerId: string;
  payerPayeeEngagementId: string;

  workItems: IWorkItem[];
  workDefinitions: IWorkDefinitionResponse[];
  resources: AcceptedValuesResources;

  contractorName?: string;
  currentUserRole?: "payer" | "payee";
  otherPartyName?: string;
  onViewHistory?: () => void;
  onEdit?: () => void;
  statusPill?: WSPillProps;
  overlay?: boolean;
};

export const CardWorkItems: React.FC<Props> = props => {
  const modalAddItem = useModalAddItem();
  const queryWorkLog = useQueryWorkLog(props.workLogId);
  const activeWorkItems = useMemo(
    () => filterActiveWorkItems(props.workItems),
    [props.workItems]
  );

  return (
    <WSElement>
      <WSList gap="M">
        <WSElement>
          <WSSectionToolbar
            title="Work summary"
            pill={props.statusPill}
            buttons={[
              ...(props.onViewHistory
                ? [
                    {
                      kind: "Link" as const,
                      label: "View history",
                      onClick: props.onViewHistory
                    }
                  ]
                : []),
              ...(props.onEdit
                ? [
                    {
                      kind: "Link" as const,
                      label: "Edit",
                      onClick: props.onEdit
                    }
                  ]
                : [])
            ]}
          />
          <WSText.ParagraphSm color="gray500">
            Work logging for this assignment is only available in U.S. Dollars
            (USD).
          </WSText.ParagraphSm>
        </WSElement>

        {activeWorkItems.length === 0 && !props.shimmer ? (
          <WSEmptyState
            type="documents"
            orientation="horizontal"
            size="S"
            title="No work added yet"
            description="Add work items to your work log to get started"
            hasBorder
            bgColor="white"
            actions={
              !props.noEdit
                ? {
                    buttons: [
                      {
                        kind: "Secondary",
                        label: "Add work",
                        onClick: () => {
                          modalAddItem.open({
                            workLogId: props.workLogId,
                            payerId: props.payerId,
                            workDefinitions: props.workDefinitions,
                            engagementId: props.payerPayeeEngagementId,
                            resources: props.resources
                          });
                        }
                      }
                    ]
                  }
                : undefined
            }
          />
        ) : (
          <WSElement
            style={props.overlay ? { position: "relative" } : undefined}
          >
            {props.overlay && (
              <WSElement
                colorBackground="gray50"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 1,
                  pointerEvents: "none",
                  borderRadius: "inherit",
                  opacity: 0.6
                }}
              />
            )}
            <WSCard p="NONE" shimmer={props.shimmer}>
              {activeWorkItems.map((workItem, index) => (
                <React.Fragment key={workItem.workItemId}>
                  {index > 0 && <WSDivider />}
                  <WSElement p="XL">
                    <Item
                      noEdit={props.noEdit}
                      payerEdit={props.payerEdit}
                      showViewHistory={!!props.onViewHistory}
                      contractorName={props.contractorName}
                      workItem={workItem}
                      workDefinitions={props.workDefinitions}
                      resources={props.resources}
                      currentUserRole={props.currentUserRole}
                      otherPartyName={props.otherPartyName}
                      workLogStatus={queryWorkLog.data?.status}
                      lastSubmittedAt={
                        (queryWorkLog.data as any)?.events?.inReviewAt
                      }
                    />
                  </WSElement>
                </React.Fragment>
              ))}

              {!props.noEdit && (
                <WSElement p="XL">
                  <WSButton.Link
                    icon="plus-circle"
                    onClick={() => {
                      modalAddItem.open({
                        workLogId: props.workLogId,
                        payerId: props.payerId,
                        workDefinitions: props.workDefinitions,
                        engagementId: props.payerPayeeEngagementId,
                        resources: props.resources
                      });
                    }}
                  >
                    Add work
                  </WSButton.Link>
                </WSElement>
              )}
            </WSCard>
            <WSCard shimmer={props.shimmer}>
              <WSFlexBox
                wrap="nowrap"
                justify="space-between"
                alignItems="center"
              >
                <WSText color="gray500" weight="medium">
                  Total amount (USD)
                </WSText>
                <WSText.Heading5>
                  {formatMoney(queryWorkLog.data?.amount || 0, "USD")}
                </WSText.Heading5>
              </WSFlexBox>
            </WSCard>
          </WSElement>
        )}
      </WSList>
    </WSElement>
  );
};
